import type { PageType } from 'types';
import type { HotDealsModule } from 'types/capi/modules';

import BarcodeIcon from '~/assets/icons/BarcodeIcon';
import ShieldIcon from '~/assets/icons/ShieldIcon';
import ShippingIcon from '~/assets/icons/Shipping';
import SoleMarketplaceLogo from '~/assets/icons/SoleMarketplaceLogo';
import Image from '~/components/Image';
import ProductCard from '~/components/ProductCard';
import Tracked from '~/components/Tracked';

const MODULE_TYPE = 'homepage-marketplace-top-sellers';

type Props = {
  backdrop: { image_desktop: string; image_mobile: string };
  items: HotDealsModule['items'];
  modulePosition?: number;
  pageType: PageType;
};

function Backdrop({
  children,
  backdrop
}: {
  backdrop: Props['backdrop'];
  children: React.ReactNode;
}) {
  return (
    <div className="relative flex w-full items-center justify-center px-4 py-10 md:px-0 md:py-20">
      <Image
        loading="eager"
        className="absolute -z-50 block size-full object-cover"
        alt="home-page-marketplace-backdrop"
        src={backdrop.image_mobile}
        responsiveOptions={{
          md: {
            src: backdrop.image_desktop
          },
          xs: {
            src: backdrop.image_mobile
          }
        }}
        progressiveLoadSettings={{ highResQuality: 100, lowResQuality: 50 }}
      />
      <div className="absolute inset-0 -z-40 bg-black opacity-40"></div>
      {children}
    </div>
  );
}

function Heading() {
  return (
    <div className="flex w-full flex-col gap-4 md:flex-row md:items-center md:justify-between">
      <h2 className="text-title-xl order-2 flex flex-col justify-start gap-2 text-left font-normal md:order-1">
        The hottest pairs on our marketplace
      </h2>
      <div className="order-1 h-[24px] md:order-2 md:h-[30px]">
        <SoleMarketplaceLogo fill="white" height="30" />
      </div>
    </div>
  );
}

type USPProps = {
  icon: React.ReactNode;
  subtitle: string;
  title: string;
};

function USP({ icon, title, subtitle }: USPProps) {
  return (
    <div className="flex shrink-0 flex-col items-start gap-2 bg-black/[0.72] px-4 py-6 max-md:w-[282px] md:flex-1 md:flex-row md:items-center md:justify-start md:gap-4">
      {icon}
      <div className="flex flex-col gap-2">
        <span className="text-2xl">{title}</span>
        <span className="text-sm leading-[22px]">{subtitle}</span>
      </div>
    </div>
  );
}

function USPs() {
  return (
    <div className="no-scrollbar relative flex items-start justify-between gap-6 self-stretch max-md:-mx-4 max-md:overflow-x-scroll max-md:px-4 md:w-full">
      <USP
        icon={<BarcodeIcon />}
        title="Legit seller & sneakers"
        subtitle="Every seller and pair is legit checked"
      />
      <USP
        icon={<ShieldIcon />}
        title="Buy with confidence"
        subtitle="Returns accepted if they’re not right"
      />
      <USP
        icon={<ShippingIcon />}
        title="Get your pair faster"
        subtitle="Orders typically delivered in 2-4 days"
      />
    </div>
  );
}

function ProductCarousel({
  items,
  modulePosition,
  pageType
}: {
  items: Props['items'];
  modulePosition?: number;
  pageType: PageType;
}) {
  return (
    <div className="no-scrollbar relative flex items-start gap-6 self-stretch overflow-x-scroll max-md:-mx-4 max-md:px-4 md:w-full">
      {items.map((product, index) => (
        <ProductCard.Internal
          key={`marketplace-product-${product.id}`}
          product={product}
          padImage
          className="flex w-[168px] shrink-0 gap-2 md:w-[264px] md:gap-6"
          imageLoading="eager"
          allowNotifications={false}
          cardSize="small"
          showNotificationCount={true}
          modulePosition={modulePosition}
          moduleType={MODULE_TYPE}
          position={index + 1}
          pageType={pageType}
          pagePosition="Homepage Marketplace"
          overrideInteractionPagePosition={true}
        />
      ))}
    </div>
  );
}

function ViewMoreButton() {
  return (
    <div className="w-36">
      <Tracked.InternalLink
        variant="transparent-white-border"
        className="flex items-center justify-center"
        pagePosition="Homepage Marketplace"
        name="VIEW_MORE"
        to={`/sole-market/`}
      >
        View more
      </Tracked.InternalLink>
    </div>
  );
}

export default function MarketplaceTopSellers({
  items,
  backdrop,
  modulePosition,
  pageType
}: Props) {
  return (
    <Backdrop backdrop={backdrop}>
      <div className="content-width flex w-full flex-col items-center justify-center gap-6 text-white md:gap-8">
        <Heading />
        <USPs />
        <ProductCarousel
          items={items}
          modulePosition={modulePosition}
          pageType={pageType}
        />
        <ViewMoreButton />
      </div>
    </Backdrop>
  );
}
